import { Check, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { buildPath } from '../routing';
import { normalizeLanguage } from '../i18n/utils';
import { trackConversion } from '../analytics';
import { ScrollReveal } from './ScrollReveal';

type FaqItem = { q: string; a: string };

interface PricingProps {
  showIntro?: boolean;
  sectionBgClassName?: string;
}

export function Pricing({
  showIntro = true,
  sectionBgClassName = 'bg-gray-50',
}: PricingProps) {
  const { t, i18n } = useTranslation();
  const currentLang = normalizeLanguage(i18n.language);
  const homePath = buildPath(currentLang, 'home');
  const contactPath = buildPath(currentLang, 'contact');

  const isPolishPricing = currentLang === 'pl';
  const currencySymbol = isPolishPricing ? 'zł' : '$';

  const standardMonthly = isPolishPricing ? 29 : 9;
  const standardAnnualTotal = isPolishPricing ? 299 : 90;
  const lifetimePrice = isPolishPricing ? 799 : 249;

  const plans = [
    {
      key: 'free',
      price: '0',
      featured: false,
    },
    {
      key: 'standard',
      price: standardMonthly.toString(),
      featured: true,
    },
    {
      key: 'lifetime',
      price: lifetimePrice.toString(),
      featured: false,
    },
  ];

  const getFeatures = (planKey: string): string[] => {
    const count = planKey === 'free' ? 5 : planKey === 'standard' ? 9 : 9;
    return Array.from({ length: count }, (_, i) => 
      t(`pricing.${planKey}.features.${i}`)
    );
  };

  const faqItemsRaw = t('pricing.faqItems', { returnObjects: true });
  const faqItems: FaqItem[] = Array.isArray(faqItemsRaw)
    ? (faqItemsRaw as unknown[]).filter((item): item is FaqItem => {
        if (!item || typeof item !== 'object') return false;
        const it = item as Record<string, unknown>;
        return typeof it.q === 'string' && typeof it.a === 'string';
      })
    : [];

  return (
    <section
      id="pricing"
      className={`py-20 ${sectionBgClassName}`}
      style={{ scrollMarginTop: '112px' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {showIntro ? (
          <div className="text-center max-w-4xl mx-auto mb-12">
            <ScrollReveal>
              <h2 className="text-4xl font-bold text-gray-900 mb-3">
                {t('pricing.title')}
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={1}>
              <p className="text-lg text-gray-600 leading-relaxed">
                {t('pricing.subtitle')}
              </p>
            </ScrollReveal>
          </div>
        ) : null}

        <div id="plans" className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan) => {
            const planDelay = plan.key === 'free' ? 1 : plan.key === 'standard' ? 2 : 3;
            return (
              <ScrollReveal key={plan.key} delay={planDelay}>
                <div
                  className={`rounded-2xl p-8 ${
                    plan.featured
                      ? 'bg-teal-600 text-white shadow-2xl scale-105 border-4 border-teal-500'
                      : 'bg-white border-2 border-gray-200'
                  }`}
                >
                  {plan.featured && (
                    <div className="inline-block bg-teal-500 text-white px-3 py-1 rounded-full text-sm font-semibold mb-4">
                      {t('pricing.standard.badge')}
                    </div>
                  )}
                  
                  <h3
                    className={`text-2xl font-bold mb-2 ${
                      plan.featured ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    {t(`pricing.${plan.key}.name`)}
                  </h3>
                  
                  <p
                    className={`text-sm mb-6 ${
                      plan.featured ? 'text-teal-100' : 'text-gray-600'
                    }`}
                  >
                    {t(`pricing.${plan.key}.description`)}
                  </p>

                  {plan.key === 'standard' && (
                    <div className="mb-4">
                      <span
                        className="inline-flex items-center text-sm font-semibold"
                        style={{
                          padding: '6px 12px',
                          borderRadius: '999px',
                          backgroundColor: plan.featured ? 'rgba(255,255,255,0.18)' : '#ecfdf5',
                          border: plan.featured ? '1px solid rgba(255,255,255,0.35)' : '1px solid #99f6e4',
                          color: plan.featured ? '#ffffff' : '#0f766e',
                        }}
                      >
                        {t('pricing.standard.annualNote')}
                      </span>
                    </div>
                  )}
                  
                  <div className={plan.key === 'standard' ? 'mb-2' : 'mb-6'}>
                    <div>
                      <span
                        className={`text-5xl font-bold ${
                          plan.featured ? 'text-white' : 'text-gray-900'
                        }`}
                        style={plan.key === 'standard' ? { fontSize: '3.75rem', lineHeight: '1' } : undefined}
                      >
                        {(plan.key === 'standard' ? standardMonthly : plan.price)} {currencySymbol}
                      </span>
                      <span
                        className={`ml-2 ${
                          plan.featured ? 'text-teal-100' : 'text-gray-600'
                        }`}
                      >
                        {plan.key === 'standard'
                          ? t('pricing.standard.period')
                          : t(`pricing.${plan.key}.period`)}
                      </span>
                    </div>
                    {plan.key === 'standard' && (
                      <div
                        className={`text-sm ${
                          plan.featured ? 'text-teal-100' : 'text-gray-600'
                        }`}
                        style={{ marginTop: '2px', marginBottom: '36px' }}
                      >
                        {t('pricing.standard.annualTotal', {
                          price: standardAnnualTotal,
                          currency: currencySymbol,
                        })}
                      </div>
                    )}
                  </div>
                  
                  <button
                    className={`w-full py-4 px-6 rounded-lg font-semibold mb-8 transition-colors flex items-center justify-center gap-2 ${
                      plan.featured
                        ? 'bg-white text-teal-600 hover:bg-gray-100'
                        : 'bg-teal-600 text-white hover:bg-teal-700'
                    }`}
                    onClick={() => trackConversion('buy_click', { plan: plan.key })}
                  >
                    <Download className="size-5" />
                    {t(`pricing.${plan.key}.cta`)}
                  </button>
                  
                  <ul className="space-y-4">
                    {getFeatures(plan.key).map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div
                          className={`rounded-full p-1 flex-shrink-0 ${
                            plan.featured ? 'bg-teal-500' : 'bg-teal-100'
                          }`}
                        >
                          <Check
                            className={`size-4 ${
                              plan.featured ? 'text-white' : 'text-teal-600'
                            }`}
                          />
                        </div>
                        <span
                          className={
                            plan.featured ? 'text-teal-50' : 'text-gray-700'
                          }
                        >
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </ScrollReveal>
            );
          })}
        </div>

        <ScrollReveal delay={4}>
          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">
              {t('pricing.contact')}
            </p>
            <a
              href={contactPath}
              className="text-teal-600 hover:text-teal-700 font-semibold underline"
            >
              {t('pricing.contactLink')}
            </a>
          </div>
        </ScrollReveal>

        <div
          className="border-t border-gray-200"
          style={{ marginTop: '48px', marginBottom: '48px' }}
          aria-hidden="true"
        />

        {faqItems.length > 0 && (
          <div style={{ marginTop: '48px' }}>
            <div className="text-center max-w-3xl mx-auto mb-8">
              <ScrollReveal>
                <h3 className="text-3xl font-semibold text-gray-900 mb-4">
                  {t('pricing.faqTitle')}
                </h3>
              </ScrollReveal>
            </div>
            <div className="grid gap-4 max-w-4xl mx-auto">
              {faqItems.map((item, idx) => (
                <ScrollReveal key={item.q} delay={(idx % 6) + 1}>
                  <div className="rounded-xl border border-gray-200 bg-white p-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">
                      {item.q}
                    </h4>
                    <p className="text-gray-600 leading-relaxed">
                      {item.a}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
